import { useState } from "react";
import { format, isPast, isToday } from "date-fns";
import {
  Package,
  DollarSign,
  CalendarDays,
  Activity,
  ArrowRight,
  AlertCircle,
  Plus,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PipelineOpportunity } from "@/types/pipeline";
import { OpportunityAssessmentCard } from "@/components/pipeline/OpportunityAssessmentCard";
import { VoiceNoteInput } from "@/components/pipeline/VoiceNoteInput";
import { useAddActivity } from "@/hooks/usePipeline";
import { toast } from "sonner";

interface StrategyOverviewTabProps {
  opportunity: PipelineOpportunity;
}

function formatCurrency(value: number | null) {
  if (!value) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function StrategyOverviewTab({ opportunity }: StrategyOverviewTabProps) {
  const addActivity = useAddActivity();
  const [quickActivityType, setQuickActivityType] = useState("note");
  const [quickActivityTitle, setQuickActivityTitle] = useState("");
  const [quickActivityDescription, setQuickActivityDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);

  const handleQuickActivity = () => {
    if (!quickActivityTitle.trim()) return;
    addActivity.mutate(
      {
        opportunity_id: opportunity.id,
        activity_type: quickActivityType,
        title: quickActivityTitle.trim(),
        description: quickActivityDescription.trim() || null,
        activity_date: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          toast.success("Activity logged");
          setQuickActivityTitle("");
          setQuickActivityDescription("");
          setShowDescription(false);
        },
        onError: () => toast.error("Failed to log activity"),
      }
    );
  };

  const nextActionOverdue =
    opportunity.next_action_date &&
    isPast(new Date(opportunity.next_action_date)) &&
    !isToday(new Date(opportunity.next_action_date));

  return (
    <div className="space-y-5">
      {/* Deal Context Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="h-3.5 w-3.5" />
              <span className="text-xs">Product</span>
            </div>
            <p className="text-sm font-semibold truncate">
              {opportunity.product_name || "Not set"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" />
              <span className="text-xs">Deal Value</span>
            </div>
            <p className="text-sm font-semibold">
              {formatCurrency(opportunity.deal_value)}
              {opportunity.revenue_regularity && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  /{opportunity.revenue_regularity === "Monthly" ? "mo" : opportunity.revenue_regularity === "Annual" ? "yr" : "once"}
                </span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="text-xs">Expected Close</span>
            </div>
            <p className="text-sm font-semibold">
              {opportunity.expected_close_date
                ? format(new Date(opportunity.expected_close_date), "MMM d, yyyy")
                : "Not set"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Activity className="h-3.5 w-3.5" />
              <span className="text-xs">Activities</span>
            </div>
            <p className="text-sm font-semibold">
              {opportunity.activity_count}
              {opportunity.last_activity_date && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  last {format(new Date(opportunity.last_activity_date), "MMM d")}
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Opportunity Summary */}
      {opportunity.opp_summary && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Summary
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {opportunity.opp_summary.split(";").map((part, i) => {
              const trimmed = part.trim();
              if (!trimmed) return null;
              const colonIdx = trimmed.indexOf(":");
              if (colonIdx > 0) {
                const key = trimmed.slice(0, colonIdx).trim();
                const val = trimmed.slice(colonIdx + 1).trim();
                return (
                  <Badge key={i} variant="outline" className="text-xs">
                    <span className="text-muted-foreground mr-1">{key}:</span> {val}
                  </Badge>
                );
              }
              return (
                <Badge key={i} variant="outline" className="text-xs">
                  {trimmed}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Single Sales Objective */}
      {opportunity.single_sales_objective && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Single Sales Objective
          </h4>
          <p className="text-sm bg-blue-50 border border-blue-200 p-3 rounded-lg whitespace-pre-wrap">
            {opportunity.single_sales_objective}
          </p>
        </div>
      )}

      {/* Assessment Card */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Assessment
        </h4>
        <OpportunityAssessmentCard
          opportunityId={opportunity.id}
          assessment={opportunity.opportunity_assessment}
        />
      </div>

      {/* Next Action */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Next Action
        </h4>
        {opportunity.next_action ? (
          <Card className={nextActionOverdue ? "border-red-300 bg-red-50/50" : ""}>
            <CardContent className="p-3 flex items-start gap-3">
              {nextActionOverdue ? (
                <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
              ) : (
                <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium">{opportunity.next_action}</p>
                {opportunity.next_action_date && (
                  <p className={`text-xs mt-0.5 ${nextActionOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                    {nextActionOverdue ? "Overdue: " : "Due: "}
                    {format(new Date(opportunity.next_action_date), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="text-sm text-muted-foreground italic">No next action set</p>
        )}
      </div>

      {/* Quick Activity Log */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Quick Log
        </h4>
        <div className="flex gap-2">
          <Select value={quickActivityType} onValueChange={setQuickActivityType}>
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="call">Call</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="meeting">Meeting</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="What happened?"
            value={quickActivityTitle}
            onChange={(e) => setQuickActivityTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !showDescription && handleQuickActivity()}
            className="flex-1 h-9"
          />
          <VoiceNoteInput
            contextId={`quick-${opportunity.id}`}
            onTranscript={(text) => {
              setQuickActivityDescription((prev) =>
                prev ? `${prev}\n\n${text}` : text
              );
              setShowDescription(true);
            }}
          />
          <Button
            size="sm"
            onClick={handleQuickActivity}
            disabled={!quickActivityTitle.trim() || addActivity.isPending}
            className="h-9"
          >
            <Plus className="h-4 w-4 mr-1" />
            Log
          </Button>
        </div>
        {showDescription && (
          <Textarea
            placeholder="Voice transcript or additional details..."
            value={quickActivityDescription}
            onChange={(e) => setQuickActivityDescription(e.target.value)}
            rows={3}
            className="text-sm mt-2"
          />
        )}
      </div>

      {/* Notes */}
      {opportunity.notes && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Notes
          </h4>
          <p className="text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">
            {opportunity.notes}
          </p>
        </div>
      )}
    </div>
  );
}
